// 사용량 추적 서비스 (DynamoDB 연동) - 향상된 버전

// Usage API 엔드포인트 (통합 API Gateway 사용)
// 환경 변수에서 가져오거나 기본값 사용
const USAGE_API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  "https://q4auaypyed.execute-api.us-east-1.amazonaws.com/prod";

// 캐시 관리
const CACHE_KEYS = {
  USAGE_DATA: "cached_usage_data",
  CACHE_TIMESTAMP: "usage_cache_timestamp",
  SYNC_QUEUE: "usage_sync_queue",
};

const CACHE_DURATION = 30000; // 30초 캐시

// 캐시 유틸리티
const cacheManager = {
  set: (key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
      localStorage.setItem(CACHE_KEYS.CACHE_TIMESTAMP, Date.now().toString());
    } catch (error) {
      console.error("캐시 저장 실패:", error);
    }
  },

  get: (key) => {
    try {
      const timestamp = localStorage.getItem(CACHE_KEYS.CACHE_TIMESTAMP);
      if (timestamp && Date.now() - parseInt(timestamp) < CACHE_DURATION) {
        const data = localStorage.getItem(key);
        return data ? JSON.parse(data) : null;
      }
      return null;
    } catch (error) {
      console.error("캐시 읽기 실패:", error);
      return null;
    }
  },

  clear: () => {
    try {
      localStorage.removeItem(CACHE_KEYS.USAGE_DATA);
      localStorage.removeItem(CACHE_KEYS.CACHE_TIMESTAMP);
    } catch (error) {
      console.error("캐시 초기화 실패:", error);
    }
  },
};

// 재시도 로직을 위한 유틸리티
const retryWithExponentialBackoff = async (
  fn,
  maxRetries = 3,
  baseDelay = 1000
) => {
  let lastError;

  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (i < maxRetries - 1) {
        const delay = baseDelay * Math.pow(2, i);
        console.log(`재시도 ${i + 1}/${maxRetries}, ${delay}ms 후 재시도...`);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
};

// 사용자 정보 가져오기 헬퍼 함수 (향상된 버전)
const getCurrentUser = () => {
  try {
    const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
    const userId = userInfo.email || userInfo.username || "ai@sedaily.com"; // 기본값을 실제 사용자로 설정

    // URL 인코딩 문제 방지
    const cleanUserId = userId.replace(/%40/g, "@").replace(/%2B/g, "+");

    // 사용자 플랜 결정 로직
    // 1. userRole이 admin이면 premium
    // 2. 기본적으로 모든 사용자는 premium (실제 결제 시스템 구현 전까지)
    // 3. 나중에 실제 플랜 정보는 서버에서 받아와야 함
    const userRole = localStorage.getItem("userRole");
    const userPlan = userRole === "admin" ? "premium" : "premium"; // 현재는 모두 premium

    return {
      userId: cleanUserId,
      plan: userPlan,
    };
  } catch (error) {
    console.error("사용자 정보 파싱 실패:", error);
    return { userId: "anonymous", plan: "free" };
  }
};

// 인증 헤더 생성 (향상된 버전)
const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken");
  const headers = {
    "Content-Type": "application/json",
    // Cache-Control 헤더 제거 (CORS 오류 방지)
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
};

// 플랜별 제한 설정 (서버에서 가져올 수도 있음)
const PLAN_LIMITS = {
  free: {
    "daily-news-analysis": {
      monthlyTokens: 10000,
      dailyMessages: 20,
      maxTokensPerMessage: 1000,
    },
    "press-release-writing": {
      monthlyTokens: 10000,
      dailyMessages: 20,
      maxTokensPerMessage: 1000,
    },
    "digital-news-writing": {
      monthlyTokens: 10000,
      dailyMessages: 20,
      maxTokensPerMessage: 1000,
    },
  },
  premium: {
    "daily-news-analysis": {
      monthlyTokens: 100000,
      dailyMessages: 100,
      maxTokensPerMessage: 2000,
    },
    "press-release-writing": {
      monthlyTokens: 100000,
      dailyMessages: 100,
      maxTokensPerMessage: 2000,
    },
    "digital-news-writing": {
      monthlyTokens: 100000,
      dailyMessages: 100,
      maxTokensPerMessage: 2000,
    },
  },
  admin: {
    "daily-news-analysis": {
      monthlyTokens: 500000,
      dailyMessages: 500,
      maxTokensPerMessage: 4000,
    },
    "press-release-writing": {
      monthlyTokens: 500000,
      dailyMessages: 500,
      maxTokensPerMessage: 4000,
    },
    "digital-news-writing": {
      monthlyTokens: 500000,
      dailyMessages: 500,
      maxTokensPerMessage: 4000,
    },
  },
};

// 동기화 큐 관리 함수들
const addToSyncQueue = (data) => {
  try {
    const queue = JSON.parse(
      localStorage.getItem(CACHE_KEYS.SYNC_QUEUE) || "[]"
    );
    queue.push(data);
    localStorage.setItem(CACHE_KEYS.SYNC_QUEUE, JSON.stringify(queue));
    console.log("동기화 큐에 추가됨, 대기 항목:", queue.length);
  } catch (error) {
    console.error("동기화 큐 추가 실패:", error);
  }
};

const getSyncQueue = () => {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEYS.SYNC_QUEUE) || "[]");
  } catch (error) {
    console.error("동기화 큐 읽기 실패:", error);
    return [];
  }
};

const clearSyncQueue = () => {
  try {
    localStorage.removeItem(CACHE_KEYS.SYNC_QUEUE);
    console.log("동기화 큐 초기화 완료");
  } catch (error) {
    console.error("동기화 큐 초기화 실패:", error);
  }
};

// 백그라운드 동기화 (네트워크 복구 시 자동 실행)
const syncPendingUsage = async () => {
  const queue = getSyncQueue();
  if (queue.length === 0) return;

  console.log(`🔄 ${queue.length}개 대기 중인 사용량 동기화 시작...`);
  const failedItems = [];

  for (const item of queue) {
    try {
      const response = await fetch(`${USAGE_API_BASE_URL}/usage/update`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(item),
      });

      if (!response.ok) {
        failedItems.push(item);
      }
    } catch (error) {
      failedItems.push(item);
    }
  }

  // 실패한 항목만 다시 큐에 저장
  if (failedItems.length > 0) {
    localStorage.setItem(CACHE_KEYS.SYNC_QUEUE, JSON.stringify(failedItems));
    console.warn(`⚠️ ${failedItems.length}개 동기화 실패, 나중에 재시도`);
  } else {
    clearSyncQueue();
    console.log("✅ 모든 대기 사용량 동기화 완료");
  }
};

// 네트워크 상태 변경 감지 및 자동 동기화
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    console.log("🌐 네트워크 연결됨 - 대기 중인 사용량 동기화 시작");
    setTimeout(syncPendingUsage, 2000); // 2초 후 동기화
  });
}

// 데이터 유효성 검사 유틸리티
const validateUsageData = (data) => {
  const errors = [];

  if (!data) {
    errors.push("데이터가 없습니다");
    return { isValid: false, errors };
  }

  // 필수 필드 검사
  const requiredFields = [
    "userId",
    "userPlan",
    "daily-news-analysis",
    "press-release-writing",
    "digital-news-writing",
  ];
  requiredFields.forEach((field) => {
    if (!data[field]) {
      errors.push(`필수 필드 누락: ${field}`);
    }
  });

  // 엔진별 데이터 검증
  [
    "daily-news-analysis",
    "press-release-writing",
    "digital-news-writing",
  ].forEach((engine) => {
    if (data[engine]) {
      const engineData = data[engine];

      // 토큰 수 검증
      if (engineData.monthlyTokensUsed < 0) {
        errors.push(`${engine}: 음수 토큰 값`);
      }

      // 한도 초과 검증
      if (
        engineData.limits &&
        engineData.monthlyTokensUsed > engineData.limits.monthlyTokens
      ) {
        console.warn(`⚠️ ${engine}: 월간 토큰 한도 초과`);
      }

      // 입출력 토큰 합계 검증
      const totalTokens =
        (engineData.inputTokens || 0) + (engineData.outputTokens || 0);
      if (totalTokens !== engineData.monthlyTokensUsed && totalTokens > 0) {
        console.warn(
          `⚠️ ${engine}: 토큰 합계 불일치 (입력+출력: ${totalTokens}, 총합: ${engineData.monthlyTokensUsed})`
        );
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings: errors.filter((e) => e.includes("⚠️")),
  };
};

// 토큰 계산 유틸리티 (간단한 추정치)
export const estimateTokens = (text) => {
  if (!text) return 0;

  // 한글: 평균 2-3자당 1토큰
  // 영어: 평균 4자당 1토큰
  const koreanChars = (text.match(/[가-힣]/g) || []).length;
  const englishChars = (text.match(/[a-zA-Z]/g) || []).length;
  const otherChars = text.length - koreanChars - englishChars;

  const koreanTokens = Math.ceil(koreanChars / 2.5);
  const englishTokens = Math.ceil(englishChars / 4);
  const otherTokens = Math.ceil(otherChars / 3);

  return koreanTokens + englishTokens + otherTokens;
};

// 글자 수 계산
export const countCharacters = (text) => {
  return text ? text.length : 0;
};

// 로컬 스토리지 키
const USAGE_KEY = "user_usage_data";
const USER_PROFILE_KEY = "user_profile";

// 사용자 프로필 가져오기
export const getUserProfile = () => {
  try {
    const stored = localStorage.getItem(USER_PROFILE_KEY);
    if (!stored) {
      // 기본값 - 현재는 모든 사용자 premium
      return {
        userId: localStorage.getItem("userId") || "anonymous",
        currentPlan: "premium", // 기본값을 premium으로 변경
        signupDate: new Date().toISOString(),
        planStartDate: new Date().toISOString(),
      };
    }
    return JSON.parse(stored);
  } catch (error) {
    console.error("프로필 로드 실패:", error);
    return {
      currentPlan: "free",
    };
  }
};

// 사용자 플랜 설정
export const setUserPlan = (plan) => {
  const profile = getUserProfile();
  profile.currentPlan = plan;
  profile.planStartDate = new Date().toISOString();
  localStorage.setItem(USER_PROFILE_KEY, JSON.stringify(profile));
  return profile;
};

// 사용량 데이터 초기화
const initializeUsageData = () => {
  const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
  const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  const userProfile = getUserProfile();
  const planLimits = PLAN_LIMITS[userProfile.currentPlan] || PLAN_LIMITS.free;

  return {
    "daily-news-analysis": {
      period: currentMonth,
      planType: userProfile.currentPlan,
      tokens: {
        input: 0,
        output: 0,
        total: 0,
      },
      characters: {
        input: 0,
        output: 0,
      },
      messageCount: 0,
      dailyUsage: {
        [today]: {
          tokens: 0,
          messages: 0,
        },
      },
      limits: planLimits["daily-news-analysis"],
      firstUsedAt: null,
      lastUsedAt: null,
    },
    "press-release-writing": {
      period: currentMonth,
      planType: userProfile.currentPlan,
      tokens: {
        input: 0,
        output: 0,
        total: 0,
      },
      characters: {
        input: 0,
        output: 0,
      },
      messageCount: 0,
      dailyUsage: {
        [today]: {
          tokens: 0,
          messages: 0,
        },
      },
      limits: planLimits["press-release-writing"],
      firstUsedAt: null,
      lastUsedAt: null,
    },
    "digital-news-writing": {
      period: currentMonth,
      planType: userProfile.currentPlan,
      tokens: {
        input: 0,
        output: 0,
        total: 0,
      },
      characters: {
        input: 0,
        output: 0,
      },
      messageCount: 0,
      dailyUsage: {
        [today]: {
          tokens: 0,
          messages: 0,
        },
      },
      limits: planLimits["digital-news-writing"],
      firstUsedAt: null,
      lastUsedAt: null,
    },
  };
};

// 로컬 사용량 데이터 가져오기
export const getLocalUsageData = () => {
  try {
    // 먼저 백업 데이터 확인 (실제 사용량)
    const backup = localStorage.getItem(USAGE_KEY + "_backup");
    if (backup) {
      try {
        const backupData = JSON.parse(backup);
        // 백업 데이터가 있으면 우선 사용
        return backupData;
      } catch (e) {
        console.log("백업 데이터 파싱 실패");
      }
    }

    const stored = localStorage.getItem(USAGE_KEY);
    if (!stored) {
      // 초기화하되, 기존 백업 데이터가 있는지 한 번 더 확인
      const existingBackup = localStorage.getItem(USAGE_KEY + "_backup");
      if (existingBackup) {
        return JSON.parse(existingBackup);
      }

      const initialData = initializeUsageData();
      localStorage.setItem(USAGE_KEY, JSON.stringify(initialData));
      return initialData;
    }

    const data = JSON.parse(stored);
    const currentMonth = new Date().toISOString().slice(0, 7);

    // 월이 바뀌었으면 초기화
    if (data["daily-news-analysis"]?.period !== currentMonth) {
      const newData = initializeUsageData();
      localStorage.setItem(USAGE_KEY, JSON.stringify(newData));
      return newData;
    }

    // 플랜 변경 체크 및 제한 업데이트
    const userProfile = getUserProfile();
    const planLimits = PLAN_LIMITS[userProfile.currentPlan] || PLAN_LIMITS.free;

    if (data["daily-news-analysis"]) {
      data["daily-news-analysis"].planType = userProfile.currentPlan;
      data["daily-news-analysis"].limits = planLimits["daily-news-analysis"];
    }
    if (data["press-release-writing"]) {
      data["press-release-writing"].planType = userProfile.currentPlan;
      data["press-release-writing"].limits =
        planLimits["press-release-writing"];
    }
    if (data["digital-news-writing"]) {
      data["digital-news-writing"].planType = userProfile.currentPlan;
      data["digital-news-writing"].limits = planLimits["digital-news-writing"];
    }

    return data;
  } catch (error) {
    console.error("사용량 데이터 로드 실패:", error);
    return initializeUsageData();
  }
};

// 사용량 제한 체크
export const checkUsageLimit = (engineType, additionalTokens = 0) => {
  const usageData = getLocalUsageData();
  const engine = usageData[engineType];
  const today = new Date().toISOString().slice(0, 10);

  if (!engine) return { allowed: false, reason: "잘못된 엔진 타입" };

  // 월간 토큰 제한 체크
  if (engine.tokens.total + additionalTokens > engine.limits.monthlyTokens) {
    return {
      allowed: false,
      reason: "월간 토큰 한도 초과",
      remaining: Math.max(0, engine.limits.monthlyTokens - engine.tokens.total),
    };
  }

  // 일일 메시지 제한 체크
  const todayUsage = engine.dailyUsage[today] || { messages: 0 };
  if (todayUsage.messages >= engine.limits.dailyMessages) {
    return {
      allowed: false,
      reason: "일일 메시지 한도 초과",
      dailyRemaining: 0,
    };
  }

  // 메시지당 토큰 제한 체크
  if (additionalTokens > engine.limits.maxTokensPerMessage) {
    return {
      allowed: false,
      reason: "메시지당 토큰 한도 초과",
      maxAllowed: engine.limits.maxTokensPerMessage,
    };
  }

  return {
    allowed: true,
    remaining: engine.limits.monthlyTokens - engine.tokens.total,
    dailyRemaining: engine.limits.dailyMessages - todayUsage.messages,
  };
};

// 사용량 업데이트 (DynamoDB API 호출) - 향상된 에러 처리
export const updateLocalUsage = async (engineType, inputText, outputText) => {
  const user = getCurrentUser();

  console.log(`📊 ${engineType} 사용량 업데이트 시도:`, {
    userId: user.userId,
    inputLength: inputText?.length || 0,
    outputLength: outputText?.length || 0,
  });

  // 네트워크 연결 상태 확인
  if (!navigator.onLine) {
    console.warn("⚠️ 오프라인 상태 - 로컬 백업 모드로 전환");
    return updateLocalUsageBackup(engineType, inputText, outputText);
  }

  try {
    // AbortController로 타임아웃 설정 (3초로 단축)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000); // 3초 타임아웃으로 단축

    let response;
    try {
      response = await fetch(`${USAGE_API_BASE_URL}/usage/update`, {
        method: "POST",
        headers: getAuthHeaders(),
        signal: controller.signal,
        body: JSON.stringify({
          userId: user.userId,
          engineType: engineType,
          inputText: inputText || "",
          outputText: outputText || "",
          userPlan: user.plan,
        }),
      });
    } catch (fetchError) {
      // 네트워크 오류 또는 타임아웃
      if (fetchError.name === "AbortError") {
        console.error("⏰ API 요청 타임아웃");
      } else {
        console.error("🌐 네트워크 오류:", fetchError.message);
      }
      throw fetchError;
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      // 4xx 에러는 재시도하지 않고 바로 로컬 백업으로
      if (response.status >= 400 && response.status < 500) {
        console.warn(
          `⚠️ 클라이언트 오류 (${response.status}) - 로컬 백업으로 전환`
        );
        return updateLocalUsageBackup(engineType, inputText, outputText);
      }
      throw new Error(`API 오류: ${response.status}`);
    }

    const result = await response.json();

    if (result.success) {
      console.log(`✅ ${engineType} 사용량 업데이트 성공:`, result);

      // 로컬 백업용으로 저장 (오프라인 대비)
      const backupData = getLocalUsageData();

      // 서버에서 반환한 누적 데이터 사용 (accumulated 필드)
      const accumulated = result.accumulated || result.data || {};

      // API 응답의 사용량 데이터를 로컬 형식으로 변환
      backupData[engineType] = {
        period: new Date().toISOString().slice(0, 7),
        planType: user.plan,
        tokens: {
          input: accumulated.inputTokens || 0,
          output: accumulated.outputTokens || 0,
          total: accumulated.totalTokens || 0,
        },
        characters: {
          input: (accumulated.inputTokens || 0) * 4, // 토큰당 약 4자 추정
          output: (accumulated.outputTokens || 0) * 4,
        },
        messageCount: accumulated.messageCount || 0,
        dailyUsage: backupData[engineType]?.dailyUsage || {},
        limits:
          PLAN_LIMITS[user.plan]?.[engineType] || PLAN_LIMITS.free[engineType],
        firstUsedAt:
          backupData[engineType]?.firstUsedAt || new Date().toISOString(),
        lastUsedAt: accumulated.lastUsedAt || new Date().toISOString(),
      };

      // 로컬 스토리지 업데이트
      localStorage.setItem(USAGE_KEY, JSON.stringify(backupData));
      localStorage.setItem(USAGE_KEY + "_backup", JSON.stringify(backupData));

      console.log(
        `📊 ${engineType} 로컬 데이터 동기화:`,
        backupData[engineType]
      );

      // 실제 퍼센티지 계산 (로컬에서 다시 계산하여 일관성 유지)
      // result.percentage는 서버에서 잘못 계산될 수 있으므로 무시하고 로컬에서 재계산
      const localPercentage = getUsagePercentage(engineType);

      return {
        success: true,
        percentage: localPercentage,
        remaining: result.remaining,
        usage: result.usage,
      };
    } else {
      console.warn(`⚠️ ${engineType} 사용량 제한:`, result.error);
      return {
        success: false,
        reason: result.error,
        remaining: result.remaining || 0,
      };
    }
  } catch (error) {
    console.error("사용량 업데이트 실패:", error);

    // API 실패 시 로컬 백업 사용
    console.log("🔄 로컬 백업으로 전환");
    return updateLocalUsageBackup(engineType, inputText, outputText);
  }
};

// API 실패 시 로컬 백업 함수 - 향상된 버전
const updateLocalUsageBackup = (engineType, inputText, outputText) => {
  try {
    console.log("💾 로컬 백업 모드로 사용량 업데이트");

    const usageData = getLocalUsageData();
    const engine = usageData[engineType];
    const today = new Date().toISOString().slice(0, 10);

    if (!engine) {
      console.error(`잘못된 엔진 타입: ${engineType}`);
      return {
        success: false,
        reason: "잘못된 엔진 타입",
        isLocalBackup: true,
      };
    }

    // 토큰 및 글자 수 계산
    const inputTokens = estimateTokens(inputText);
    const outputTokens = estimateTokens(outputText);
    const totalTokens = inputTokens + outputTokens;
    const inputChars = countCharacters(inputText);
    const outputChars = countCharacters(outputText);

    // 사용량 제한 체크
    const limitCheck = checkUsageLimit(engineType, totalTokens);
    if (!limitCheck.allowed) {
      return {
        success: false,
        reason: limitCheck.reason,
        ...limitCheck,
      };
    }

    // 토큰 업데이트
    engine.tokens.input += inputTokens;
    engine.tokens.output += outputTokens;
    engine.tokens.total += totalTokens;

    // 글자 수 업데이트
    engine.characters.input += inputChars;
    engine.characters.output += outputChars;

    // 메시지 카운트 업데이트
    engine.messageCount += 1;

    // 일일 사용량 업데이트
    if (!engine.dailyUsage[today]) {
      engine.dailyUsage[today] = { tokens: 0, messages: 0 };
    }
    engine.dailyUsage[today].tokens += totalTokens;
    engine.dailyUsage[today].messages += 1;

    // 시간 업데이트
    const now = new Date().toISOString();
    if (!engine.firstUsedAt) {
      engine.firstUsedAt = now;
    }
    engine.lastUsedAt = now;

    // 로컬 백업 저장
    localStorage.setItem(USAGE_KEY + "_backup", JSON.stringify(usageData));

    console.log(`💾 ${engineType} 로컬 백업 업데이트:`, {
      inputTokens,
      outputTokens,
      totalTokens: engine.tokens.total,
    });

    // 나중에 서버와 동기화할 수 있도록 대기 큐에 추가
    addToSyncQueue({
      engineType,
      inputText,
      outputText,
      timestamp: now,
      userId: getCurrentUser().userId,
    });

    return {
      success: true,
      usage: engine,
      percentage: getUsagePercentage(engineType),
      remaining: limitCheck.remaining,
      isLocalBackup: true,
      message: "로컬에 임시 저장됨 (온라인 시 자동 동기화)",
    };
  } catch (error) {
    console.error("로컬 백업 업데이트 실패:", error);
    return {
      success: false,
      reason: "업데이트 실패",
      isLocalBackup: true,
    };
  }
};

// 사용량 퍼센티지 계산
export const getUsagePercentage = (engineType) => {
  const usageData = getLocalUsageData();
  const engine = usageData[engineType];

  if (!engine || !engine.limits) return 0;

  // tokens.total이 없으면 monthlyTokensUsed 사용 (getAllUsageData에서 오는 데이터 호환)
  const totalTokens = engine.tokens?.total ?? engine.monthlyTokensUsed ?? 0;

  // 소수점 한 자리까지 표시 (예: 0.9%)
  const percentage = (totalTokens / engine.limits.monthlyTokens) * 100;
  const rounded = Math.round(percentage * 10) / 10; // 소수점 첫째 자리까지 반올림
  return Math.min(rounded, 100); // 100% 초과 방지
};

// 사용량 요약 정보 가져오기
export const getUsageSummary = (engineType) => {
  const usageData = getLocalUsageData();
  const engine = usageData[engineType];
  const today = new Date().toISOString().slice(0, 10);

  if (!engine) return null;

  const todayUsage = engine.dailyUsage[today] || { tokens: 0, messages: 0 };

  return {
    // 퍼센티지
    percentage: getUsagePercentage(engineType),

    // 토큰 정보
    tokens: {
      used: engine.tokens.total,
      limit: engine.limits.monthlyTokens,
      remaining: Math.max(0, engine.limits.monthlyTokens - engine.tokens.total),
      input: engine.tokens.input,
      output: engine.tokens.output,
    },

    // 글자 수 정보
    characters: {
      input: engine.characters.input,
      output: engine.characters.output,
      total: engine.characters.input + engine.characters.output,
    },

    // 메시지 정보
    messages: {
      total: engine.messageCount,
      todayCount: todayUsage.messages,
      todayLimit: engine.limits.dailyMessages,
      todayRemaining: Math.max(
        0,
        engine.limits.dailyMessages - todayUsage.messages
      ),
    },

    // 플랜 정보
    plan: {
      type: engine.planType,
      limits: engine.limits,
    },

    // 시간 정보
    period: engine.period,
    lastUsed: engine.lastUsedAt,
    firstUsed: engine.firstUsedAt,
  };
};

// 모든 엔진의 사용량 통계 가져오기
export const getAllUsageStats = () => {
  const usageData = getLocalUsageData();
  const userProfile = getUserProfile();

  return {
    user: {
      userId: userProfile.userId,
      plan: userProfile.currentPlan,
      signupDate: userProfile.signupDate,
      planStartDate: userProfile.planStartDate,
    },
    engines: {
      "daily-news-analysis": getUsageSummary("daily-news-analysis"),
      "press-release-writing": getUsageSummary("press-release-writing"),
      "digital-news-writing": getUsageSummary("digital-news-writing"),
    },
    total: {
      tokens: {
        total:
          (usageData["daily-news-analysis"]?.tokens?.total || 0) +
          (usageData["press-release-writing"]?.tokens?.total || 0) +
          (usageData["digital-news-writing"]?.tokens?.total || 0),
        input:
          (usageData["daily-news-analysis"]?.tokens?.input || 0) +
          (usageData["press-release-writing"]?.tokens?.input || 0) +
          (usageData["digital-news-writing"]?.tokens?.input || 0),
        output:
          (usageData["daily-news-analysis"]?.tokens?.output || 0) +
          (usageData["press-release-writing"]?.tokens?.output || 0) +
          (usageData["digital-news-writing"]?.tokens?.output || 0),
      },
      characters: {
        input:
          (usageData["daily-news-analysis"]?.characters?.input || 0) +
          (usageData["press-release-writing"]?.characters?.input || 0) +
          (usageData["digital-news-writing"]?.characters?.input || 0),
        output:
          (usageData["daily-news-analysis"]?.characters?.output || 0) +
          (usageData["press-release-writing"]?.characters?.output || 0) +
          (usageData["digital-news-writing"]?.characters?.output || 0),
      },
      messages:
        (usageData["daily-news-analysis"]?.messageCount || 0) +
        (usageData["press-release-writing"]?.messageCount || 0) +
        (usageData["digital-news-writing"]?.messageCount || 0),
    },
  };
};

// 서버에서 사용량 데이터 가져오기 (API 호출)
export const fetchUsageFromServer = async (userId, engineType) => {
  try {
    const token = localStorage.getItem("authToken");
    const encodedUserId = encodeURIComponent(userId);
    const response = await fetch(
      `${USAGE_API_BASE_URL}/usage/${encodedUserId}/${engineType}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      }
    );

    if (!response.ok) {
      throw new Error("사용량 데이터 조회 실패");
    }

    const result = await response.json();
    console.log(`📊 서버에서 가져온 ${engineType} 사용량:`, result);
    console.log(`🔍 서버 데이터 파싱:`, {
      totalTokens: result.totalTokens,
      tokens: result.tokens,
      inputTokens: result.inputTokens,
      outputTokens: result.outputTokens,
      messageCount: result.messageCount
    });

    // 서버 응답 형식을 로컬 형식으로 변환
    const localData = getLocalUsageData();
    const serverData = result.success ? result.data : result;

    if (serverData) {
      // 사용자 플랜 확인
      const user = getCurrentUser();
      const planLimits = PLAN_LIMITS[user.plan] || PLAN_LIMITS.free;

      // API에서 받은 실제 데이터 사용 (하드코딩 제거)
      let actualTokens = parseInt(serverData.totalTokens) || parseInt(serverData.tokens) || 0;
      let actualInputTokens = parseInt(serverData.inputTokens) || 0;
      let actualOutputTokens = parseInt(serverData.outputTokens) || 0;
      let actualMessageCount = parseInt(serverData.messageCount) || 0;

      console.log('✅ 실제 API 데이터 사용:', {
        engineType,
        actualTokens, 
        actualInputTokens, 
        actualOutputTokens, 
        actualMessageCount
      });

      // 로컬 형식으로 변환 (limits 포함!)
      localData[engineType] = {
        ...localData[engineType],
        tokens: {
          input: actualInputTokens,
          output: actualOutputTokens,
          total: actualTokens,
        },
        messageCount: actualMessageCount,
        lastUsedAt: serverData.lastUsedAt,
        limits: planLimits[engineType], // 중요: limits 추가!
      };

      // 로컬 스토리지 업데이트
      localStorage.setItem(USAGE_KEY, JSON.stringify(localData));
      localStorage.setItem(USAGE_KEY + "_backup", JSON.stringify(localData));

      console.log(
        `✅ ${engineType} 로컬 데이터 동기화 완료:`,
        localData[engineType]
      );

      // 퍼센티지 계산 확인 (소수점 한 자리까지)
      const percentage =
        Math.round(
          (localData[engineType].tokens.total /
            localData[engineType].limits.monthlyTokens) *
            100 *
            10
        ) / 10;
      console.log(
        `📊 ${engineType} 사용률: ${percentage}% (${localData[engineType].tokens.total}/${localData[engineType].limits.monthlyTokens})`
      );
    }

    return serverData;
  } catch (error) {
    console.error("서버 사용량 조회 실패:", error);
    return getLocalUsageData()[engineType];
  }
};

// 서버에 사용량 업데이트 (API 호출)
export const updateUsageOnServer = async (
  userId,
  engineType,
  inputText = "",
  outputText = ""
) => {
  try {
    const token = localStorage.getItem("authToken");
    const response = await fetch(`${USAGE_API_BASE_URL}/usage`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : undefined,
      },
      body: JSON.stringify({
        userId,
        engineType,
        inputText,
        outputText,
      }),
    });

    if (!response.ok) {
      throw new Error("사용량 업데이트 실패");
    }

    return await response.json();
  } catch (error) {
    console.error("서버 사용량 업데이트 실패:", error);
    return null;
  }
};

// 플랜 변경
export const changePlan = async (newPlan) => {
  try {
    const token = localStorage.getItem("access_token");
    const userId = localStorage.getItem("userId");

    // 서버에 플랜 변경 요청
    const response = await fetch(`${USAGE_API_BASE_URL}/user/plan`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        userId,
        newPlan,
        changeDate: new Date().toISOString(),
      }),
    });

    if (!response.ok) {
      throw new Error("플랜 변경 실패");
    }

    // 로컬 프로필 업데이트
    setUserPlan(newPlan);

    // 사용량 데이터 리셋 (선택적)
    const usageData = getLocalUsageData();
    localStorage.setItem(USAGE_KEY, JSON.stringify(usageData));

    return await response.json();
  } catch (error) {
    console.error("플랜 변경 실패:", error);
    return null;
  }
};

// 사용량 초기화 (월별 리셋)
export const resetMonthlyUsage = () => {
  const initialData = initializeUsageData();
  localStorage.setItem(USAGE_KEY, JSON.stringify(initialData));
  cacheManager.clear(); // 캐시도 함께 초기화
  return initialData;
};

// 캐시 강제 초기화 (디버깅용)
export const clearUsageCache = () => {
  cacheManager.clear();
  console.log("✅ 사용량 캐시가 초기화되었습니다");
};

// 사용량 데이터 강제 새로고침
export const refreshUsageData = async () => {
  clearUsageCache();
  return getAllUsageData(true); // forceRefresh = true
};

// Dashboard에서 사용하는 전체 사용량 데이터 가져오기 (DynamoDB API) - 향상된 버전
export const getAllUsageData = async (forceRefresh = false) => {
  try {
    // 캐시 확인
    if (!forceRefresh) {
      const cachedData = cacheManager.get(CACHE_KEYS.USAGE_DATA);
      if (cachedData) {
        console.log("📊 캐시된 사용량 데이터 사용");
        return cachedData;
      }
    }

    const user = getCurrentUser();

    console.log(`📊 전체 사용량 데이터 조회: ${user.userId}`);
    console.log("📊 현재 사용자 정보:", user);

    // 재시도 로직과 함께 API 호출
    const fetchWithRetry = async (url) => {
      return retryWithExponentialBackoff(async () => {
        const response = await fetch(url, {
          method: "GET",
          headers: getAuthHeaders(),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return response.json();
      });
    };

    // 각 엔진별 사용량 호출 (재시도 로직 포함)
    const [dailyNewsResult, pressReleaseResult, digitalNewsResult] =
      await Promise.allSettled([
        fetchWithRetry(
          `${USAGE_API_BASE_URL}/usage/${encodeURIComponent(
            user.userId
          )}/daily-news-analysis`
        ),
        fetchWithRetry(
          `${USAGE_API_BASE_URL}/usage/${encodeURIComponent(
            user.userId
          )}/press-release-writing`
        ),
        fetchWithRetry(
          `${USAGE_API_BASE_URL}/usage/${encodeURIComponent(
            user.userId
          )}/digital-news-writing`
        ),
      ]);

    // 결과 처리 (실패한 요청도 안전하게 처리)
    const safeResult = (result, defaultValue) => {
      if (result.status === "fulfilled") {
        return result.value;
      } else {
        console.error("API 호출 실패:", result.reason);
        return defaultValue;
      }
    };

    const dailyNewsData = safeResult(dailyNewsResult, {
      monthlyTokensUsed: 0,
      dailyMessagesCount: 0,
      lastResetDate: new Date().toISOString(),
    });
    const pressReleaseData = safeResult(pressReleaseResult, {
      monthlyTokensUsed: 0,
      dailyMessagesCount: 0,
      lastResetDate: new Date().toISOString(),
    });
    const digitalNewsData = safeResult(digitalNewsResult, {
      monthlyTokensUsed: 0,
      dailyMessagesCount: 0,
      lastResetDate: new Date().toISOString(),
    });

    console.log("📊 API 응답 상태:");
    console.log("  - Daily News Analysis:", dailyNewsResult.status);
    console.log("  - Press Release Writing:", pressReleaseResult.status);
    console.log("  - Digital News Writing:", digitalNewsResult.status);

    // 데이터 유효성 검사 및 정규화
    const validateAndNormalizeData = (data) => {
      if (!data || typeof data !== "object") {
        return null;
      }

      // success 속성이 있는 경우 (API 응답 형식)
      if ("success" in data) {
        return data.success ? data.data : null;
      }

      // 직접 데이터가 반환된 경우
      return data;
    };

    const dailyNewsNormalized = validateAndNormalizeData(dailyNewsData);
    const pressReleaseNormalized = validateAndNormalizeData(pressReleaseData);
    const digitalNewsNormalized = validateAndNormalizeData(digitalNewsData);

    console.log("📊 정규화된 데이터:");
    console.log("  - Daily News Analysis:", dailyNewsNormalized);
    console.log("  - Press Release Writing:", pressReleaseNormalized);
    console.log("  - Digital News Writing:", digitalNewsNormalized);

    // 대시보드 호환 형식으로 변환
    const result = {
      userId: user.userId,
      userPlan: user.plan,
      signupDate: new Date().toISOString(),
      "daily-news-analysis": {
        monthlyTokensUsed:
          Number(dailyNewsNormalized?.tokens) ||
          Number(dailyNewsNormalized?.totalTokens) ||
          0,
        inputTokens: Number(dailyNewsNormalized?.inputTokens) || 0,
        outputTokens: Number(dailyNewsNormalized?.outputTokens) || 0,
        charactersProcessed: 0,
        messageCount: dailyNewsNormalized?.messageCount || 0,
        lastUsedAt: dailyNewsNormalized?.lastUsedAt,
        limits:
          PLAN_LIMITS[user.plan]?.["daily-news-analysis"] ||
          PLAN_LIMITS.free["daily-news-analysis"],
      },
      "press-release-writing": {
        monthlyTokensUsed:
          Number(pressReleaseNormalized?.tokens) ||
          Number(pressReleaseNormalized?.totalTokens) ||
          0,
        inputTokens: Number(pressReleaseNormalized?.inputTokens) || 0,
        outputTokens: Number(pressReleaseNormalized?.outputTokens) || 0,
        charactersProcessed: 0,
        messageCount: pressReleaseNormalized?.messageCount || 0,
        lastUsedAt: pressReleaseNormalized?.lastUsedAt,
        limits:
          PLAN_LIMITS[user.plan]?.["press-release-writing"] ||
          PLAN_LIMITS.free["press-release-writing"],
      },
      "digital-news-writing": {
        monthlyTokensUsed:
          Number(digitalNewsNormalized?.tokens) ||
          Number(digitalNewsNormalized?.totalTokens) ||
          0,
        inputTokens: Number(digitalNewsNormalized?.inputTokens) || 0,
        outputTokens: Number(digitalNewsNormalized?.outputTokens) || 0,
        charactersProcessed: 0,
        messageCount: digitalNewsNormalized?.messageCount || 0,
        lastUsedAt: digitalNewsNormalized?.lastUsedAt,
        limits:
          PLAN_LIMITS[user.plan]?.["digital-news-writing"] ||
          PLAN_LIMITS.free["digital-news-writing"],
      },
    };

    console.log("📊 최종 변환 결과:", result);

    // 데이터 유효성 검사
    const validation = validateUsageData(result);
    if (!validation.isValid) {
      console.error("❌ 사용량 데이터 유효성 검사 실패:", validation.errors);
    }
    if (validation.warnings && validation.warnings.length > 0) {
      console.warn("⚠️ 사용량 데이터 경고:", validation.warnings);
    }

    // 캐시에 저장 (유효한 데이터만)
    if (validation.isValid) {
      cacheManager.set(CACHE_KEYS.USAGE_DATA, result);
    }

    return result;
  } catch (error) {
    console.error("사용량 데이터 조회 실패:", error);
    // 오류 시 기본값 반환
    const user = getCurrentUser();
    return {
      userId: user.userId,
      userPlan: user.plan,
      signupDate: new Date().toISOString(),
      "daily-news-analysis": {
        monthlyTokensUsed: 0,
        inputTokens: 0,
        outputTokens: 0,
        charactersProcessed: 0,
        messageCount: 0,
        lastUsedAt: null,
        limits:
          PLAN_LIMITS[user.plan]?.["daily-news-analysis"] ||
          PLAN_LIMITS.free["daily-news-analysis"],
      },
      "press-release-writing": {
        monthlyTokensUsed: 0,
        inputTokens: 0,
        outputTokens: 0,
        charactersProcessed: 0,
        messageCount: 0,
        lastUsedAt: null,
        limits:
          PLAN_LIMITS[user.plan]?.["press-release-writing"] ||
          PLAN_LIMITS.free["press-release-writing"],
      },
      "digital-news-writing": {
        monthlyTokensUsed: 0,
        inputTokens: 0,
        outputTokens: 0,
        charactersProcessed: 0,
        messageCount: 0,
        lastUsedAt: null,
        limits:
          PLAN_LIMITS[user.plan]?.["digital-news-writing"] ||
          PLAN_LIMITS.free["digital-news-writing"],
      },
    };
  }
};

// 플랜별 제한 가져오기
export const getPlanLimits = (plan) => {
  return PLAN_LIMITS[plan] || PLAN_LIMITS.free;
};

export default {
  estimateTokens,
  countCharacters,
  getUserProfile,
  setUserPlan,
  getLocalUsageData,
  checkUsageLimit,
  updateLocalUsage,
  getUsagePercentage,
  getUsageSummary,
  getAllUsageStats,
  fetchUsageFromServer,
  updateUsageOnServer,
  changePlan,
  resetMonthlyUsage,
  getAllUsageData,
  getPlanLimits,
  clearUsageCache,
  refreshUsageData,
  syncPendingUsage,
  getSyncQueue,
  clearSyncQueue,
  PLAN_LIMITS,
};
